import { Capacitor } from '@capacitor/core'
import type { Stufe } from './rollen'

/**
 * AVV-Annahme (Vorgang 026) — die reine Seite im Client.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  DIESE DATEI ENTSCHEIDET NICHTS, WAS DER SERVER ENTSCHEIDET
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Welche Fassung aktuell ist, ob ein Stichtag verstrichen ist, ob eine Annahme
 * gilt und welche Bytes angenommen werden, steht in der Datenbank
 * (`supabase/migrations/20261021100000_v026_avv_fundament.sql`). Hier steht nur,
 * was die Oberfläche dafür braucht: Feldbezeichnungen, Fehlertexte, den Kanal,
 * den Hash zur Transportprüfung und den Anzeigezustand aus dem Serverstatus.
 * `avvZustand` rechnet deshalb keinen Stichtag und liest keine Uhr — sie
 * übernimmt `pflicht_aktiv` vom Server (N1).
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  ZWEI LISTEN, DIE ES ZWEIMAL GIBT — mit Absicht, und bewacht
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `AVV_PLATZHALTER` und `AVV_PFLICHTUEBERSCHRIFTEN` stehen zeichengleich in der
 * Migration (`avv_platzhalter()`, `avv_pflichtueberschriften()`). Die Datenbank
 * prüft damit beim Einspielen einer Fassung, das Repo prüft damit die Dateien
 * unter `docs/avv/`, bevor sie je eine Migration werden. `avv.test.ts` liest
 * die Migrationsdatei und vergleicht beide Listen; wer eine ändert, ohne die
 * andere mitzunehmen, sieht den Test rot (TA4, T09c, T11b).
 *
 * Die Platzhaltermuster sind **Postgres-Muster** (ARE), nicht JavaScript:
 * `\m`/`\M` sind dort Wortanfang und Wortende. `platzhalterRegex()` übersetzt
 * sie für die Repo-Prüfung; die Liste selbst bleibt im Wortlaut der Datenbank,
 * sonst gäbe es keinen zeichengleichen Vergleich.
 */

/** Die drei Platzhaltermuster, zeichengleich mit `avv_platzhalter()`. */
export const AVV_PLATZHALTER: readonly string[] = [
  '\\{\\{[^}]*\\}\\}',
  '\\[\\[[^\\]]*\\]\\]',
  '\\m(KUNDE_FIRMA|KUNDE_ANSCHRIFT|KUNDE_VERTRETER|PLATTFORM)\\M',
]

/**
 * Die fünf Pflichtteile aus AGB-Ziffer 14.1 als ganze Zeilen, Kern zuerst —
 * zeichengleich mit `avv_pflichtueberschriften()`, einschließlich des
 * Halbgeviertstrichs `–` (U+2013).
 */
export const AVV_PFLICHTUEBERSCHRIFTEN: readonly string[] = [
  'Vertrag über die Auftragsverarbeitung nach Art. 28 DSGVO',
  'Anlage 1 – Produktanlage',
  'Anlage 2 – Technische und organisatorische Maßnahmen',
  'Anlage 3 – Dienstleister- und Transferregister',
  'Anlage 4 – Löschregelung',
]

/** Die beiden einzigen Felder, die eine Vorlage enthalten darf. */
export const AVV_FELD_FIRMA = '{{VP_FIRMA}}'
export const AVV_FELD_ANSCHRIFT = '{{VP_ANSCHRIFT}}'

export type AvvSprache = 'de' | 'en'
export type AvvKanal = 'web' | 'ios' | 'android'

/** Die Feldschlüssel in der Reihenfolge, in der der Server sie im `hint` nennt. */
export const AVV_FELDER = ['firma', 'strasse', 'plz', 'ort', 'land'] as const
export type AvvFeld = (typeof AVV_FELDER)[number]

/**
 * Die Fehlerkennungen der Datenbank (ADR, Fehlertabelle). Jede hat in
 * `avvFehlerText` einen Satz in beiden Sprachen; `avv.test.ts` liest die
 * Migrationen und wird rot, sobald dort eine Kennung ohne Text steht (T10e).
 */
export const AVV_FEHLERKENNUNGEN = [
  'avv_annahme_fehlt',
  'avv_fassung_veraltet',
  'avv_hash_abweichend',
  'avv_vertragspartner_unvollstaendig',
  'avv_vertragspartner_zu_lang',
  'avv_vertragspartner_zeichen',
  'avv_platzhalter_offen',
  'avv_kanal_ungueltig',
  'avv_email_fehlt',
  'avv_nur_owner',
  'avv_fassung_unvollstaendig',
  'avv_fassung_format',
  'avv_fassung_unveraenderlich',
  'avv_nachweis_unveraenderlich',
  'avv_weg_ungueltig',
] as const
export type AvvFehlerkennung = (typeof AVV_FEHLERKENNUNGEN)[number]

/* ────────────────────────────────────────────────────────── Bezeichnungen */

const FELD_BEZEICHNUNG: Record<AvvSprache, Record<AvvFeld, string>> = {
  de: { firma: 'Firmenname', strasse: 'Straße und Hausnummer', plz: 'PLZ', ort: 'Ort', land: 'Land' },
  en: { firma: 'Company name', strasse: 'Street and number', plz: 'Postcode', ort: 'Town', land: 'Country' },
}

/**
 * Die sichtbare Bezeichnung eines Partnerfelds. `AvvSchritt` (Schnitt 3)
 * beschriftet seine Felder hiermit, damit die Fehlermeldung dasselbe Wort
 * nennt wie das Feld (TA1). Straße, PLZ und Ort heißen wie im Buchungsformular.
 */
export function avvFeldBezeichnung(feld: AvvFeld, sprache: AvvSprache): string {
  return FELD_BEZEICHNUNG[sprache][feld]
}

function istFeld(s: string): s is AvvFeld {
  return (AVV_FELDER as readonly string[]).includes(s)
}

/** `'strasse,plz'` → die Bezeichnungen in Serverreihenfolge; Unbekanntes fällt weg. */
function felderAusHint(hint: string | null | undefined, sprache: AvvSprache): string[] {
  if (!hint) return []
  const genannt = new Set(hint.split(',').map((s) => s.trim()).filter(istFeld))
  return AVV_FELDER.filter((f) => genannt.has(f)).map((f) => avvFeldBezeichnung(f, sprache))
}

/*
 * Nur Kommas, kein „und": „Straße und Hausnummer" trägt selbst eines, und
 * „Straße und Hausnummer und PLZ" liest sich wie zwei Felder statt drei.
 */
function aufzaehlung(teile: string[]): string {
  return teile.join(', ')
}

/* ────────────────────────────────────────────────────────── Fehlertexte */

type Satz = { de: string; en: string }

const ALLGEMEIN: Satz = {
  de: 'Das hat nicht geklappt. Bitte versuchen Sie es noch einmal.',
  en: 'That did not work. Please try again.',
}

/** Sätze ohne Feldbezug. Die Feldkennungen stehen darunter in `FELDSATZ`. */
const SATZ: Record<AvvFehlerkennung, Satz> = {
  avv_annahme_fehlt: {
    de: 'Für die Einrichtung ist die Annahme des Auftragsverarbeitungsvertrags nötig. Bitte aktualisieren Sie StaySuite.',
    en: 'Setting up requires accepting the data processing agreement. Please update StaySuite.',
  },
  avv_fassung_veraltet: {
    de: 'Inzwischen gilt eine neue Fassung des Vertrags. Bitte lesen Sie sie und bestätigen Sie erneut.',
    en: 'A new version of the agreement is now in force. Please read it and confirm again.',
  },
  avv_hash_abweichend: {
    de: 'Der angezeigte Vertragstext stimmt nicht mit dem gespeicherten überein. Bitte laden Sie den Vertrag neu und bestätigen Sie erneut.',
    en: 'The agreement shown does not match the stored one. Please reload the agreement and confirm again.',
  },
  avv_vertragspartner_unvollstaendig: {
    de: 'Bitte ergänzen Sie die Angaben zu Ihrem Betrieb.',
    en: 'Please complete the details of your business.',
  },
  avv_vertragspartner_zu_lang: {
    de: 'Eine Angabe ist zu lang (höchstens 200 Zeichen).',
    en: 'One entry is too long (200 characters at most).',
  },
  avv_vertragspartner_zeichen: {
    de: 'Eine Angabe ist nicht lesbar oder enthält unzulässige Steuer- oder Richtungszeichen. Bitte tippen Sie sie neu ein.',
    en: 'One entry is not readable or contains disallowed control or direction characters. Please type it again.',
  },
  avv_platzhalter_offen: {
    de: 'Der Vertragstext ist gerade nicht vollständig. Bitte versuchen Sie es später noch einmal.',
    en: 'The agreement text is not complete at the moment. Please try again later.',
  },
  avv_kanal_ungueltig: {
    de: 'Die App konnte Ihr Gerät nicht zuordnen. Bitte laden Sie die Seite neu und bestätigen Sie erneut.',
    en: 'The app could not identify your device. Please reload the page and confirm again.',
  },
  avv_email_fehlt: {
    de: 'Zu Ihrem Konto ist keine E-Mail-Adresse hinterlegt. Bitte wenden Sie sich an den Support.',
    en: 'There is no email address on your account. Please contact support.',
  },
  avv_nur_owner: {
    de: 'Den Vertrag kann nur der Inhaber des Betriebs annehmen.',
    en: 'Only the owner of the business can accept the agreement.',
  },
  avv_fassung_unvollstaendig: {
    de: 'Der Vertrag ist gerade nicht verfügbar. Bitte versuchen Sie es später noch einmal.',
    en: 'The agreement is not available at the moment. Please try again later.',
  },
  avv_fassung_format: {
    de: 'Der Vertrag ist gerade nicht verfügbar. Bitte versuchen Sie es später noch einmal.',
    en: 'The agreement is not available at the moment. Please try again later.',
  },
  avv_fassung_unveraenderlich: {
    de: 'Eine geltende Vertragsfassung lässt sich nicht mehr ändern.',
    en: 'An agreement version in force can no longer be changed.',
  },
  avv_nachweis_unveraenderlich: {
    de: 'Eine abgegebene Annahme lässt sich nicht mehr ändern.',
    en: 'An acceptance once given can no longer be changed.',
  },
  avv_weg_ungueltig: {
    de: 'Die Annahme konnte nicht zugeordnet werden. Bitte versuchen Sie es noch einmal.',
    en: 'The acceptance could not be assigned. Please try again.',
  },
}

/** Sätze für Kennungen, deren `hint` Feldschlüssel trägt. `{felder}` = Bezeichnungen. */
const FELDSATZ: Partial<Record<AvvFehlerkennung, Satz>> = {
  avv_vertragspartner_unvollstaendig: {
    de: 'Bitte ergänzen Sie: {felder}.',
    en: 'Please fill in: {felder}.',
  },
  avv_vertragspartner_zu_lang: {
    de: 'Zu lang, höchstens 200 Zeichen: {felder}.',
    en: 'Too long, 200 characters at most: {felder}.',
  },
  avv_vertragspartner_zeichen: {
    de: 'Bitte tippen Sie lesbar und ohne unzulässige Steuer- oder Richtungszeichen neu ein: {felder}.',
    en: 'Please type again with readable text and without disallowed control or direction characters: {felder}.',
  },
  avv_platzhalter_offen: {
    de: 'Bitte entfernen Sie doppelte geschweifte oder eckige Klammern und Platzhalterbegriffe aus: {felder}.',
    en: 'Please remove double curly or square brackets and placeholder terms from: {felder}.',
  },
}

function istKennung(s: string): s is AvvFehlerkennung {
  return (AVV_FEHLERKENNUNGEN as readonly string[]).includes(s)
}

/**
 * Der Satz zu einer Fehlerkennung des Servers (`detail`) in der
 * Oberflächensprache (TA1, TA6). Trägt der `hint` Feldschlüssel, nennt der Satz
 * jedes betroffene Feld mit seiner sichtbaren Bezeichnung. Die Kennung selbst
 * erscheint nie — auch eine unbekannte nicht, die bekommt den allgemeinen Satz.
 */
export function avvFehlerText(
  kennung: string | null | undefined,
  hint: string | null | undefined,
  sprache: AvvSprache,
): string {
  if (!kennung || !istKennung(kennung)) return ALLGEMEIN[sprache]
  const felder = felderAusHint(hint, sprache)
  const feldsatz = FELDSATZ[kennung]
  if (feldsatz && felder.length > 0) {
    return feldsatz[sprache].replace('{felder}', aufzaehlung(felder))
  }
  return SATZ[kennung][sprache]
}

/* ──────────────────────────────────────────────────── Kanal und Hash */

/** Der Kanal des Nachweises — eine Angabe des Clients, kein Beweis (ADR-Risiko). */
export function avvKanal(): AvvKanal {
  const p = Capacitor.getPlatform()
  return p === 'ios' || p === 'android' ? p : 'web'
}

/**
 * SHA-256 als Kleinbuchstaben-Hex über die UTF-8-Bytes — dieselben Bytes wie
 * `convert_to(…, 'UTF8')` in der Datenbank. Im Client nur eine
 * Transportprüfung: der Hash im Nachweis kommt immer vom Server.
 */
export async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

/* ──────────────────────────────────────────────────────── Anzeigezustand */

/** Die Antwort von `avv_status(p_tenant_id)`. */
export interface AvvStatus {
  fassung: { kennung: string; titel: string; annahme_pflicht_ab: string } | null
  angenommen: boolean
  pflicht_aktiv: boolean
  darf_annehmen: boolean
}

export type AvvZustand = 'keiner' | 'hinweis' | 'gate' | 'banner_inhaber'

/**
 * Was die Oberfläche zeigt (A1, A3, N1).
 *
 *   keiner          — keine Fassung, schon angenommen, oder kein Status
 *                     (ein Fehler der Statusabfrage öffnet, sperrt nicht)
 *   hinweis         — Owner, Stichtag noch nicht verstrichen
 *   gate            — Owner, `pflicht_aktiv` vom Server
 *   banner_inhaber  — jede andere Rolle, vor UND nach dem Stichtag, ohne Sperre
 */
export function avvZustand(status: AvvStatus | null, rolle: Stufe | null): AvvZustand {
  if (!status || !status.fassung || status.angenommen) return 'keiner'
  if (rolle !== 'owner') return 'banner_inhaber'
  return status.pflicht_aktiv ? 'gate' : 'hinweis'
}

/* ─────────────────────────────────────────────────────────────── Land */

/**
 * Das Land des Vertragspartners als Wort im deutschen Vertrag (A7) — für die
 * Codes der Länderauswahl der Ersteinrichtung. `OTHER` nimmt den Freitext.
 * Ein unbekannter Code ergibt '' und zählt damit beim Server als fehlend; ein
 * erfundenes Land wäre schlimmer als keins (AK10).
 */
const LAENDER: Record<string, string> = {
  AT: 'Österreich',
  DE: 'Deutschland',
  CH: 'Schweiz',
  IT: 'Italien',
  GB: 'Vereinigtes Königreich',
  IE: 'Irland',
}

export function landName(code: string, freitext?: string | null): string {
  if (code === 'OTHER') return (freitext ?? '').trim()
  return LAENDER[code] ?? ''
}

/* ───────────────────────────────────────────── Vorlagenprüfung im Repo */

/**
 * Ein Postgres-Platzhaltermuster als JavaScript-RegExp. `\m`/`\M` werden zu
 * Lookarounds über Buchstaben, Ziffern und `_` — das ist der Wortbegriff von
 * Postgres, und mit `u` gilt er auch für Umlaute.
 */
export function platzhalterRegex(muster: string): RegExp {
  const js = muster.replace(/\\m/g, '(?<![\\p{L}\\p{N}_])').replace(/\\M/g, '(?![\\p{L}\\p{N}_])')
  return new RegExp(js, 'gu')
}

/** Alle Platzhalterfunde in einem Text, Muster für Muster — wie `avv_platzhalter()`. */
export function avvPlatzhalter(text: string): string[] {
  return AVV_PLATZHALTER.flatMap((m) => Array.from(text.matchAll(platzhalterRegex(m)), (t) => t[0]))
}

/**
 * Prüft den Text einer Vorlage wie der Trigger `avv_fassungen_pruefen` — für
 * `docs/avv/*.txt`, bevor daraus eine Migration wird. `de` ist die verbindliche
 * Fassung (mit Pflichtüberschriften), `en` die Übersetzung (ohne). Liefert die
 * Befunde als Sätze; leer heißt in Ordnung. Den Dateinamen setzt der Aufrufer
 * davor.
 */
export function avvVorlageFehler(text: string, art: AvvSprache): string[] {
  const fehler: string[] = []
  if (text.includes('﻿')) fehler.push('enthält ein BOM (U+FEFF)')
  if (text.includes('\r')) fehler.push('enthält CR, erlaubt ist nur LF')
  if (text.normalize('NFC') !== text) fehler.push('ist nicht in Unicode-Normalform NFC')
  if (!text.includes(AVV_FELD_FIRMA)) fehler.push(`ohne Feld ${AVV_FELD_FIRMA}`)
  if (!text.includes(AVV_FELD_ANSCHRIFT)) fehler.push(`ohne Feld ${AVV_FELD_ANSCHRIFT}`)

  // Beide Felder in EINEM Durchgang heraus, wie regexp_replace im Trigger — sonst
  // würde aus `{{{{VP_FIRMA}}VP_ANSCHRIFT}}` erst `{{VP_ANSCHRIFT}}` und dann nichts.
  // Danach ist jeder Klammerrest ein Fund, auch ohne Gegenstück.
  const rest = text.replace(/\{\{VP_(FIRMA|ANSCHRIFT)\}\}/g, '')
  const funde = [...avvPlatzhalter(rest), ...['{{', '}}', '[[', ']]'].filter((k) => rest.includes(k))]
  if (funde.length > 0) fehler.push(`enthält Platzhalter: ${funde.join(', ')}`)

  if (art === 'de') {
    const zeilen = new Set(text.split('\n'))
    for (const h of AVV_PFLICHTUEBERSCHRIFTEN) {
      if (!zeilen.has(h)) fehler.push(`fehlt als eigene Zeile: ${h}`)
    }
    if (text.split('\n')[0] !== AVV_PFLICHTUEBERSCHRIFTEN[0]) {
      fehler.push(`erste Zeile ist nicht die Kern-Überschrift: ${AVV_PFLICHTUEBERSCHRIFTEN[0]}`)
    }
  }
  return fehler
}
